import BrandLogo from '@/components/BrandLogo';
import StandaloneSurface from '@/components/Layout/StandaloneSurface';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AppIcon } from '@/components/ui/icons';
import { Button } from '@/features/ui/components/Button';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Fail-closed recovery surface for an authenticated session whose AAL or factor
 * state could not be resolved. Retrying happens through a clean session restore;
 * the product shell never mounts while assurance is unknown.
 */
export default function MfaUnavailable() {
  const { cancelMfa, pendingMfaEmail } = useAuth();

  return (
    <StandaloneSurface measure="reading">
      <div className="space-y-6">
        <BrandLogo variant="auth" className="mx-auto" />
        <Card variant="elevated">
          <CardHeader className="items-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-status-warning-soft">
              <AppIcon name="shieldAlert" className="h-5 w-5 text-status-warning-foreground" />
            </div>
            <CardTitle className="mt-3">Security verification unavailable</CardTitle>
            {pendingMfaEmail && <p className="text-sm text-text-muted">{pendingMfaEmail}</p>}
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="warning">
              <AppIcon name="alertCircle" className="h-4 w-4" />
              <AlertTitle>Your workspace remains protected</AlertTitle>
              <AlertDescription>
                AX1 could not confirm this session&apos;s authenticator assurance level. No workspace data or controls were loaded.
              </AlertDescription>
            </Alert>
            <p className="text-sm leading-6 text-text-muted">
              Sign in again to retry. If the issue continues, contact your workspace administrator or AX1 support.
            </p>
            <Button type="button" size="lg" fullWidth onClick={cancelMfa}>
              Return to sign in
            </Button>
          </CardContent>
        </Card>
      </div>
    </StandaloneSurface>
  );
}
